/**
 * A folha que sobe de baixo — o `showModalBottomSheet` do Flutter.
 *
 * É um `<dialog>` de verdade e não uma `<div>` com `position: fixed`: o
 * navegador já prende o teclado dentro dela, já fecha no Esc, já escurece o
 * fundo e já avisa o leitor de tela que o resto da página saiu do caminho.
 * Refazer isso à mão daria trinta linhas de `useEffect` para chegar pior.
 *
 * O conteúdo só existe enquanto ela está aberta, de propósito: no Flutter a
 * folha é construída na hora em que abre, então um formulário meio preenchido
 * e abandonado não volta na próxima vez.
 */

import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import './folha.css';

interface Props {
  aberta: boolean;
  aoFechar: () => void;
  titulo: string;
  /** A linha em cinza embaixo do título, quando a folha explica o que faz. */
  recado?: string;
  /** Altura fixa, que é como o seletor de receita abre lá (75% da tela). */
  alta?: boolean;
  /**
   * No meio da tela em vez de no pé, para o que lá é `showDialog`: o
   * formulário de marcar um compromisso abre assim no Flutter.
   */
  centro?: boolean;
  /**
   * Uma classe a mais no `<dialog>`, para quando a folha de uma tela tem
   * medidas próprias no Flutter — o editor de calendários, por exemplo, tem
   * título em branco e não em rosa-quartzo.
   */
  classe?: string;
  children: ReactNode;
}

export function Folha({ aberta, aoFechar, titulo, recado, alta, centro, classe, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const folha = ref.current;
    if (!folha) return;
    if (aberta && !folha.open) folha.showModal();
    else if (!aberta && folha.open) folha.close();
  }, [aberta]);

  // O clique no escuro chega como um clique no próprio `<dialog>`, porque o
  // fundo é pseudo-elemento dele. É assim que dá para separar "clicou fora" de
  // "clicou num botão lá dentro" sem envolver nenhum ouvinte no documento.
  function noFundo(e: MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) aoFechar();
  }

  return (
    <dialog
      ref={ref}
      className={`folha${alta ? ' alta' : ''}${centro ? ' centro' : ''}${classe ? ` ${classe}` : ''}`}
      aria-label={titulo}
      onClick={noFundo}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
    >
      {aberta ? (
        <div className="folha-corpo">
          <div className="folha-alca" aria-hidden="true" />
          <h2 className="folha-titulo">{titulo}</h2>
          {recado ? <p className="discreto folha-recado">{recado}</p> : null}
          {children}
        </div>
      ) : null}
    </dialog>
  );
}
